import React, { useState, useEffect } from "react";
import { Check, ChevronRight, Loader2, Ban } from "lucide-react";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { DetailsSection } from "~/components/assistant/messages/DetailsSection";
import { ToolCall } from "~/components/assistant/types";
import { cn } from "~/lib/utils";

interface ToolMessageProps {
  toolCall: ToolCall;
  expanded?: boolean;
}

export function ToolMessage({ toolCall, expanded }: ToolMessageProps) {
  const [isExpanded, setIsExpanded] = useState(expanded ?? false);

  // Sync with controlled expanded prop
  useEffect(() => {
    if (expanded !== undefined) {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const hasArgs = toolCall.args && Object.keys(toolCall.args).length > 0;

  return (
    <div className="border border-gray-200 rounded-lg p-2">
      {hasArgs ? (
        <details
          className="space-y-3"
          open={isExpanded}
          onToggle={(e) => {
            const target = e.target as HTMLDetailsElement;
            setIsExpanded(target.open);
            e.stopPropagation();
          }}
        >
          <summary className={cn("list-none cursor-pointer mb-0 select-none")}>
            <div className="flex items-center gap-1.5 justify-between">
              <div className="flex gap-2 items-center">
                {toolCall.running ? (
                  <Loader2 className="size-3.5 text-purple-600 animate-spin" />
                ) : toolCall.cancelled ? (
                  <Ban className="size-3.5 text-purple-600" />
                ) : (
                  <Check className="size-3.5 text-purple-600" />
                )}
                <span className="text-xs font-medium text-purple-600">
                  {toolCall.toolName}
                  {toolCall.cancelled && " (cancelled)"}
                </span>
              </div>
              <ChevronRight
                className={cn(
                  "size-4 text-purple-600 transition-transform duration-200",
                  isExpanded && "rotate-90"
                )}
              />
            </div>
          </summary>

          <div className="p-2 pb-0">
            <div className="space-y-1">
              {/* Show parsed args content */}
              {toolCall.args.instructions && (
                <>
                  <DetailField label="Instructions" content={toolCall.args.instructions} />

                  {(toolCall.content?.summary || toolCall.content?.raw) && (
                    <DetailField
                      label="Response"
                      content={toolCall.content.summary || toolCall.content.raw || ""}
                    />
                  )}
                </>
              )}
              <div className="mt-1">
                <DetailsSection
                  content={
                    <div className="space-y-3 mb-1">
                      <DetailField label="Arguments" content={toolCall.args} />
                      <DetailField label="Content" content={toolCall.content} />
                    </div>
                  }
                  defaultExpanded={false}
                />
              </div>
            </div>
          </div>
        </details>
      ) : (
        <div className="flex gap-2 items-center">
          {toolCall.running ? (
            <Loader2 className="size-3.5 text-purple-600 animate-spin" />
          ) : toolCall.cancelled ? (
            <Ban className="size-3.5 text-purple-600" />
          ) : (
            <Check className="size-3.5 text-purple-600" />
          )}
          <span className="text-xs font-medium text-purple-600">
            {toolCall.toolName}
            {toolCall.cancelled && " (cancelled)"}
          </span>
        </div>
      )}
    </div>
  );
}
